import { memo } from 'react';
import { UploadCloud, X } from 'lucide-react';

export const MAX_FILE_SIZE_IN_MB = 3.5;
export const MAX_FILE_SIZE = MAX_FILE_SIZE_IN_MB * 1024 * 1024;

/**
 * Format file size for display
 */
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Uploaded files preview component
 * Shows list of files ready to be sent with the message
 */
const UploadedFiles = memo(({ uploadedFiles, setUploadedFiles, removeFile }) => {
  if (uploadedFiles.length === 0) return null;

  const hasOversizedFiles = uploadedFiles.some(file => file.size > MAX_FILE_SIZE);

  return (
    <div className="mb-1 p-3 bg-slate-100 dark:bg-neutral-800 rounded-xl border border-slate-300 dark:border-neutral-700">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-neutral-600 dark:text-neutral-400 flex items-center">
          <UploadCloud size={12} className="mr-1" />
          Ready to send
        </p>
        <button
          onClick={() => setUploadedFiles([])}
          className="text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
        >
          Clear all
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {uploadedFiles.map((file, index) => {
          const isOversized = file.size > MAX_FILE_SIZE;
          return (
            <div
              key={index}
              className={`flex items-center gap-2 px-3 py-1.5 bg-slate-200 dark:bg-neutral-900 rounded-lg text-xs border-2 ${
                isOversized
                  ? 'border-red-500 dark:border-red-400'
                  : 'border-slate-300 dark:border-neutral-700'
              }`}
            >
              <div className="flex flex-col">
                <span className={`max-w-[150px] truncate ${
                  isOversized
                    ? 'text-red-700 dark:text-red-300'
                    : 'text-neutral-700 dark:text-neutral-200'
                }`}>
                  {file.name}
                </span>
                <span className={`text-[10px] ${
                  isOversized
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-neutral-500 dark:text-neutral-400'
                }`}>
                  {formatFileSize(file.size)}
                </span>
              </div>
              <button
                onClick={() => removeFile(index)}
                className="text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
              >
                <X size={12} />
              </button>
            </div>
          );
        })}
      </div>

      {hasOversizedFiles && (
        <p className="mt-2 text-[10px] italic text-red-600 dark:text-red-400">
          Maximum size is {MAX_FILE_SIZE_IN_MB} MB. Please remove oversized files before sending.
        </p>
      )}
    </div>
  );
});

UploadedFiles.displayName = 'UploadedFiles';

export default UploadedFiles;